// ─── Shared ──────────────────────────────────────────────────────────────────

export interface ConfigmanagerConflictResolution {
  path?: string;
  revision?: string;
}

export interface ConfigManagerMergeConflict {
  path?: string;
  base_revision?: string;
  branch_revision?: string;
  target_revision?: string;
}

// ─── Branches ────────────────────────────────────────────────────────────────

export class ConfigManagerBranch {
  name: string;
  description?: string;
  owner?: string;
  updated_user?: string;
  created_at?: number;
  updated_at?: number;
  protected?: boolean;
  environments?: string[];
  linked?: boolean;
  version?: string;

  static create(
    branch: Partial<ConfigManagerBranch> = {},
  ): ConfigManagerBranch {
    return Object.assign(new ConfigManagerBranch(), branch);
  }

  static fork(
    branch: ConfigManagerBranch,
    useBranchName: boolean,
  ): ConfigManagerBranch {
    const branchName = useBranchName ? 'your-branch-name-here' : branch.name;
    return Object.assign(new ConfigManagerBranch(), branch, {
      name: `${branchName}-fork`,
    });
  }
}

export interface ConfigManagerDeleteBranchRequest {
  branch: string;
}

export interface ConfigManagerForkBranchRequest {
  source: string;
  name: string;
  links: boolean;
  description: string;
  project?: string;
}

export interface ConfigManagerMergeResponse {
  branch?: string;
  target_branch?: string;
  source_branch?: string;
  revision?: string;
  common_ancestor_sha?: string;
  conflicts?: ConfigManagerMergeConflict[];
}

export type ConfigManagerDryMergeResponse = ConfigManagerMergeResponse;

// ─── Configs (Files) ─────────────────────────────────────────────────────────

export interface ConfigManagerMetadata {
  username?: string;
  timestamp?: number;
}

export interface ConfigManagerConfig {
  name?: string;
  description?: string;
  owner?: ConfigManagerMetadata;
  last?: ConfigManagerMetadata;
  linked?: boolean;
}

export interface ConfigManagerAddConfigRequest {
  branch: string;
  name: string;
  description?: string;
  data: Record<string, object[]>;
  metadata?: ConfigMetadataLegacy[];
}

export interface ConfigManagerCloneConfigRequest {
  branch: string;
  name: string;
  description?: string;
  data?: Record<string, object[]>;
  metadata?: ConfigMetadataLegacy[];
  source: string;
}

export interface ConfigManagerRenameConfigRequest {
  branch: string;
  file: string;
  name: string;
  description: string;
}

export interface ConfigManagerDeleteConfigRequest {
  branch: string;
  file: string;
}

// ─── Sections ────────────────────────────────────────────────────────────────

export interface MetadataColumnLegacy {
  name?: string;
  position?: number;
  validation: string;
}

export interface MetadataCellLegacy {
  __bsid?: string;
  row?: number;
  col: number;
  color?: string;
  comment?: {
    value: string;
  };
}

export interface MetadataExcludedLegacy {
  name: string;
  position: number;
  data: (number | string)[];
}

export interface ConfigMetadataLegacy {
  cells?: MetadataCellLegacy[];
  columns?: MetadataColumnLegacy[];
  excluded?: MetadataExcludedLegacy[];
  sort?: {
    key?: string;
    ascending?: boolean;
  };
}

export interface ConfigLegacyLink {
  id: string;
  gid: number;
}

export interface ConfigLegacy {
  data: object[];
  metadata: ConfigMetadataLegacy;
  link?: ConfigLegacyLink;
}

export interface ConfigManagerAddSectionRequest {
  local: string;
  file: string;
  section_name: string;
  data: object[];
  metadata: ConfigMetadataLegacy;
}

export interface ConfigManagerPutSectionRequest {
  branch: string;
  config: string;
  section: string;
  body: ConfigManagerPutSectionBody;
}

export interface ConfigManagerRenameSectionRequest {
  local: string;
  file: string;
  section: string;
  section_name: string;
}

export interface ConfigManagerDeleteSectionRequest {
  branch: string;
  config: string;
  section: string;
}

export interface ConfigManagerPutSectionBody {
  data?: object[];
  metadata?: ConfigMetadataLegacy;
}

export interface ConfigManagerRenameSectionBody {
  action: 'rename';
  section_name: string;
}

export interface ConfigManagerAddSectionResponse {
  position?: number;
}

// ─── Links (Google Drive) ─────────────────────────────────────────────────────

export interface ConfigManagerConfigLink {
  name: string;
  sections: ConfigManagerSectionLink[];
}

export interface ConfigManagerSectionLink {
  name: string;
  id: string;
  gid: number;
}

export interface ConfigManagerUpdateLinkRequest {
  name: string;
  id: string;
  gid: number;
}

// ─── Check Runs ──────────────────────────────────────────────────────────────

export interface ConfigManagerCheckRun {
  name: string;
  status: string;
  conclusion: string;
  html_url: string;
}

// ─── Branch Comparison ───────────────────────────────────────────────────────

export interface ConfigManagerBranchComparison {
  base_sha: string;
  head_sha: string;
  status: string;
  ahead_by: number;
  behind_by: number;
  diff: string;
}

// ─── Dynamic Environments ──────────────────────────────────────────────────

export interface GonfigManagerEnvironment {
  identifier: string;
  name: string;
  project: string;
  product: string;
  status: string;
  endpoint: string;
  endpoint_http11: string;
  repository: string;
  config_repository: string;
  cmbranch: string;
  config_branch: string;
  branch: string;
  chart: string;
  chart_repository: string;
  chart_version: string;
  commit: string;
  status_reason: string;
  status_log: string;
  status_failing_pod: string;
  facebook_app_id: string;
  facebook_app_secret: string;
  facebook_app_namespace: string;
  is_production: boolean;
  statics_server: string;
  localizations_server: string;
  expiration: number;
  timestamp: number;
  user: string;
  linkage: string;
  sync_backend: boolean;
  is_grpc: boolean;
  manage_cmbranch: boolean;
  terminating: boolean;
  gitsync: boolean;
  overwrite: Record<string, string>;
}

export interface GonfigManagerCreateEnvironmentRequest {
  identifier: string;
  branch: string;
  cmbranch?: string;
  chart?: string;
  chart_version?: string;
  sync_backend: boolean;
  user: string;
  facebook_app_id: string;
  facebook_app_secret?: string;
  facebook_app_namespace?: string;
  statics_server: string;
  localizations_server: string;
  is_production: boolean;
  expiration: number;
  linkage?: number;
  overwrite?: Record<string, string>;
}

export interface GonfigManagerUpdateEnvironmentRequest {
  user: string;
  branch?: string;
  cmbranch?: string;
  expiration?: number;
}

// ─── Releases ──────────────────────────────────────────────────────────────

export interface GonfigManagerRelease {
  id: number;
  tag_name: string;
  name: string;
  body: string;
  prerelease: boolean;
  draft: boolean;
  target_commitish: string;
  html_url: string;
  created_at: string;
  author: string;
}

export interface GonfigManagerReleaseCommit {
  sha: string;
  message: string;
  author: string;
  date: string;
}

export interface GonfigManagerCreateReleaseRequest {
  tag_name: string;
  target_commitish: string;
  name: string;
  body: string;
  prerelease: boolean;
}

export interface GonfigManagerUpdateReleaseRequest {
  name?: string;
  body?: string;
  prerelease?: boolean;
  make_latest?: boolean;
}

export interface GonfigManagerCreatePullRequestRequest {
  name: string;
  title: string;
  body: string;
}

export interface GonfigManagerCreatePullRequestResponse {
  pull_request_url: string;
}
